import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BookingFormData } from '@/shared/types';
import { snapCheckIn, snapCheckOut } from '@/shared/utils/monthRange';

interface CreateGuestBookingResponse {
  id: string;
  reference: string;
}

/**
 * Creates a public (guest) booking via the createGuestBooking Cloud Function.
 *
 * The booking form is used by unauthenticated visitors, who cannot read the
 * bookings collection (Firestore rules require auth). Reference-uniqueness must
 * therefore be checked server-side, so creation goes through a callable that
 * runs privileged, generates a collision-checked reference, writes the doc, and
 * returns { id, reference } for the success dialog + payment email.
 *
 * checkIn/checkOut are snapped to whole months here (billing window); the exact
 * days the student picked are sent as arrivalDate/departureDate for the admin.
 */
export const useCreateBooking = () => {
  const queryClient = useQueryClient();
  const createGuestBookingFn = httpsCallable<
    Record<string, unknown>,
    CreateGuestBookingResponse
  >(getFunctions(undefined, 'europe-west1'), 'createGuestBooking');

  return useMutation({
    mutationFn: async (
      data: BookingFormData & { unitRoomTypeIds?: string[] }
    ): Promise<CreateGuestBookingResponse> => {
      if (!data.checkIn || !data.checkOut) {
        throw new Error('Check-in and check-out dates are required');
      }

      const snappedCheckIn = snapCheckIn(data.checkIn);
      const snappedCheckOut = snapCheckOut(data.checkOut);

      const result = await createGuestBookingFn({
        residenceId: data.residenceId,
        roomTypeId: data.roomTypeId,
        // All units of the typology; the server picks a genuinely free one and
        // rejects if every unit is taken for the window.
        unitRoomTypeIds: data.unitRoomTypeIds,
        checkIn: snappedCheckIn.toISOString(),
        checkOut: snappedCheckOut.toISOString(),
        // Exact days the student picked — operational info for the admin.
        arrivalDate: data.checkIn.toISOString(),
        departureDate: data.checkOut.toISOString(),
        numberOfOccupants: data.numberOfOccupants ?? 1,
        guests: data.guests,
        notes: data.notes || '',
      });

      return result.data;
    },
    onSuccess: () => {
      // Invalidate bookings query to refresh data
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    }
  });
};
